import { useState } from 'react';

import { ArrowUp, Image as ImageIcon, MessageSquare, Phone, RotateCw, X } from 'lucide-react';

import type { WidgetPreviewBranding } from './WidgetPreview';

interface Props {
  branding: WidgetPreviewBranding;
  configName?: string;
}

type View = 'closed' | 'welcome' | 'chat';

const parseStyleString = (style?: string): React.CSSProperties => {
  if (!style) return {};
  const result: Record<string, string> = {};
  style.split(';').forEach((decl) => {
    const idx = decl.indexOf(':');
    if (idx === -1) return;
    const rawKey = decl.slice(0, idx).trim();
    const value = decl.slice(idx + 1).trim();
    if (!rawKey || !value) return;
    result[rawKey.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())] = value;
  });
  return result as React.CSSProperties;
};

/** Static preview of the v2 "glass" widget (mirrors apps/widget/src/widget-v2.ts). */
export const WidgetPreviewV2 = ({ branding, configName }: Props) => {
  const [view, setView] = useState<View>('welcome');

  const pc = branding.primary_color || '#2563eb';
  const title = branding.title || configName || 'Chat';
  const logoStyle = parseStyleString(branding.logo_style);
  const heading = branding.welcome_heading?.trim() || 'How can we help?';
  const questions = (branding.suggested_questions ?? []).map((q) => q.trim()).filter(Boolean);

  const logo = (className: string) =>
    branding.logo_url ? (
      <img
        src={branding.logo_url}
        alt=""
        className={className}
        style={{ objectFit: 'contain', ...logoStyle }}
      />
    ) : null;

  const avatar = (size: number) => (
    <div
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-900/10 bg-white font-bold text-white"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        ...(branding.logo_url ? {} : { background: pc, border: 'none' }),
      }}
    >
      {logo('h-3/4 w-3/4') ?? title.charAt(0).toUpperCase()}
    </div>
  );

  const inputBox = (
    <div
      className="flex w-full items-center gap-1 rounded-2xl bg-white py-1.5 pr-1.5 pl-4"
      style={{ border: `1.5px solid ${pc}4d`, boxShadow: `0 0 0 4px ${pc}12` }}
    >
      <span className="flex-1 truncate py-2 text-left text-[13px] text-slate-400">
        Need help? Just type what you&apos;re looking for.
      </span>
      <ImageIcon size={16} className="mx-1.5 text-slate-400" />
      <Phone size={16} className="mx-1.5 text-slate-400" />
      <span
        className="flex h-8 w-8 items-center justify-center rounded-full text-white"
        style={{ background: pc }}
      >
        <ArrowUp size={15} strokeWidth={2.5} />
      </span>
    </div>
  );

  const tabs: { key: View; label: string }[] = [
    { key: 'closed', label: 'Closed' },
    { key: 'welcome', label: 'Welcome' },
    { key: 'chat', label: 'Conversation' },
  ];

  return (
    <div className="relative w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-2">
        <span className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
          Live Preview · v2
        </span>
        <div className="flex items-center gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setView(tab.key)}
              className={`rounded px-2 py-1 text-[11px] ${
                view === tab.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="relative h-[600px] w-full"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 15%,rgba(148,163,184,0.35),transparent 45%),radial-gradient(circle at 80% 70%,rgba(203,213,225,0.6),transparent 50%)',
        }}
      >
        {/* Mock host page */}
        <div className="pointer-events-none absolute inset-6 space-y-4 opacity-60">
          <div className="h-8 w-40 rounded bg-slate-300" />
          <div className="h-48 rounded-xl bg-slate-300/70" />
          <div className="grid grid-cols-3 gap-4">
            <div className="h-24 rounded-lg bg-slate-300/70" />
            <div className="h-24 rounded-lg bg-slate-300/70" />
            <div className="h-24 rounded-lg bg-slate-300/70" />
          </div>
        </div>

        {view === 'closed' && (
          <button
            type="button"
            onClick={() => setView('welcome')}
            className="absolute right-6 bottom-6 flex h-[58px] items-center gap-3 rounded-full border border-slate-900/10 bg-white/90 py-1 pr-5 pl-1 text-left shadow-xl transition-transform hover:-translate-y-0.5"
            aria-label="Open chat"
          >
            <span
              className="relative flex h-12 w-12 items-center justify-center rounded-full text-white"
              style={
                branding.logo_url
                  ? { background: '#fff', border: '1px solid rgba(15,23,42,0.08)' }
                  : { background: `linear-gradient(135deg,${pc},${pc}cc)` }
              }
            >
              {logo('h-[72%] w-[72%] rounded-full') ?? <MessageSquare size={22} />}
              <span className="absolute right-0 bottom-0 h-3 w-3 rounded-full border-2 border-white bg-green-500" />
            </span>
            <span className="flex flex-col leading-tight">
              <strong className="text-[14px] font-bold text-slate-900">Ask us a question</strong>
              <span className="text-[11px] text-slate-500">We usually reply instantly</span>
            </span>
          </button>
        )}

        {view !== 'closed' && (
          <div
            className="absolute top-4 right-4 bottom-4 flex w-[400px] max-w-[calc(100%-32px)] flex-col overflow-hidden rounded-[22px] border border-white/70 shadow-2xl"
            style={{
              background: 'rgba(255,255,255,0.84)',
              backdropFilter: 'blur(24px) saturate(180%)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-900/[0.08] bg-white/60 py-3 pr-3 pl-5">
              {logo('max-h-[28px] max-w-[160px]') ?? (
                <span className="truncate text-[15px] font-bold text-slate-900">{title}</span>
              )}
              <div className="flex items-center gap-1 text-slate-500">
                <button
                  type="button"
                  onClick={() => setView('welcome')}
                  className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[12px] font-medium hover:bg-slate-900/5"
                >
                  <RotateCw size={13} /> Start over
                </button>
                <button
                  type="button"
                  onClick={() => setView('closed')}
                  className="rounded-lg p-1.5 hover:bg-slate-900/5"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {view === 'welcome' ? (
                <div className="flex flex-col items-center pt-2 text-center">
                  <div className="mb-4">{avatar(64)}</div>
                  <p className="mb-1 text-xl font-bold tracking-tight text-slate-900">{heading}</p>
                  {branding.welcome_message && (
                    <p className="mb-5 max-w-[300px] text-[13px] text-slate-500">
                      {branding.welcome_message}
                    </p>
                  )}
                  {inputBox}
                  {questions.length > 0 && (
                    <>
                      <p className="mt-5 mb-2.5 text-[12.5px] text-slate-500">
                        Or start with a quick question
                      </p>
                      <div className="flex w-full flex-col gap-2">
                        {questions.map((q) => (
                          <button
                            key={q}
                            type="button"
                            onClick={() => setView('chat')}
                            className="flex w-full items-center gap-2.5 rounded-xl border border-slate-900/[0.08] bg-white/70 px-3.5 py-2.5 text-left text-[13px] font-medium text-slate-700 hover:bg-white"
                          >
                            <MessageSquare size={14} className="shrink-0 text-slate-400" />
                            {q}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="flex justify-end">
                    <span
                      className="rounded-[20px] rounded-br-md px-4 py-2.5 text-[13px] font-medium text-white"
                      style={{
                        background: `linear-gradient(135deg,${pc},${pc}cc)`,
                        boxShadow: `0 6px 16px ${pc}38`,
                      }}
                    >
                      {questions[0] || 'Any SUV offers?'}
                    </span>
                  </div>
                  <div className="flex items-start gap-2.5">
                    {avatar(30)}
                    <div className="flex min-w-0 flex-col gap-1.5">
                      <p className="text-[11px] text-slate-400">
                        <strong className="font-semibold text-slate-700">{title}</strong> · now
                      </p>
                      <div className="rounded-[20px] rounded-tl-md border border-slate-900/5 bg-white/90 px-3.5 py-3 text-[13px] leading-relaxed text-gray-800 shadow-sm">
                        <p className="mb-2">
                          Yes, we currently have offers on several models. Here are the highlights:
                        </p>
                        <ul className="mb-2 list-disc space-y-1 pl-5 marker:text-slate-400">
                          <li>
                            <strong className="font-semibold text-gray-900">From £299/month</strong>{' '}
                            on selected SUVs
                          </li>
                          <li>0% APR on nearly-new stock</li>
                        </ul>
                        <p>
                          Would you like to{' '}
                          <span className="font-medium underline" style={{ color: pc }}>
                            book a test drive
                          </span>
                          ?
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom */}
            <div className="px-4 pb-2.5">
              {view === 'chat' && <div className="pt-3">{inputBox}</div>}
              <p className="mt-2 text-center text-[11px] text-slate-400">
                Powered by <strong className="font-semibold text-slate-500">Carpass.ai</strong>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
